import type {CSSProperties} from 'react';
import {WEDGE_SHELL as B} from '@territory/tokens';
import {control, font, radius, space, TOUCH_MIN, weight} from '../scale.js';

export type WedgeSigninLook = 'native-apple' | 'native-light' | 'web-google' | 'web-microsoft' | 'web-apple' | 'alternate' | 'fallback-google';
// Released provider geometry and timing, off-scale. Native Google and Microsoft share the light look.
const PROVIDER_FONT = 15.5;
const SECONDARY_FONT = 14.5;
const PROVIDER_HEIGHT = 52;
const PROVIDER_GAP = 10;
const PROVIDER_MS = 160;
const ENTRANCE_MS = 520;
const ENTRANCE_DELAY = 720;
const GLASS_BLUR = 12;
const STAGGER_Y = 12;
const ALTERNATE_FONT = 13.5;
const ALTERNATE_PY = 11;
const ALTERNATE_PX = 14;

export function wedgeSigninStyle(look: WedgeSigninLook, pending: boolean): CSSProperties {
  /* The released email fallback omits shadow, transition, opacity and entrance animation. */
  if (look === 'fallback-google') return {
    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: PROVIDER_GAP,
    padding: `13px ${space[5]}px`, borderRadius: radius.xl, border: 0, background: B.signinWhite, color: B.signinGoogleInk,
    fontSize: font.body, fontWeight: weight.bold, cursor: 'pointer', fontFamily: 'inherit', minHeight: control.field,
  };
  /* Released Apple-orphan doors omit motion, opacity, flex alignment and disabled paint. */
  if (look === 'alternate') return {
    width: '100%', padding: `${ALTERNATE_PY}px ${ALTERNATE_PX}px`, borderRadius: radius.md,
    border: `1px solid ${B.alternateBorder}`, background: B.alternateSurface, color: B.text,
    fontSize: ALTERNATE_FONT, fontWeight: weight.semibold, cursor: 'pointer', fontFamily: 'inherit', minHeight: TOUCH_MIN,
  };
  const native = look === 'native-apple' || look === 'native-light';
  const secondary = look === 'web-microsoft' || look === 'web-apple';
  return {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: PROVIDER_GAP,
    padding: `${secondary ? 13 : space[4]}px ${space[5]}px`,
    borderRadius: radius.xl,
    border: look === 'native-apple' ? `1px solid ${B.signinNativeDarkBorder}`
      : look === 'native-light' ? `1px solid ${B.signinNativeLightBorder}`
      : look === 'web-microsoft' ? `1px solid ${B.signinWebBorder}`
      : look === 'web-apple' ? `1px solid ${B.signinAppleBorder}` : 0,
    background: look === 'native-apple' ? B.signinBlack : look === 'web-microsoft' ? B.signinWebSurface : look === 'web-apple' ? B.surface : B.signinWhite,
    color: look === 'native-apple' ? B.signinWhite : look === 'native-light' ? B.signinNativeInk : look === 'web-google' ? B.signinGoogleInk : B.text,
    fontSize: secondary ? SECONDARY_FONT : PROVIDER_FONT,
    fontWeight: secondary ? weight.semibold : weight.bold,
    cursor: pending ? 'wait' : 'pointer',
    fontFamily: 'inherit',
    opacity: pending ? 0.85 : 1,
    ...(!secondary ? {boxShadow: B.signinShadow, transition: `transform ${PROVIDER_MS}ms ${B.ease}`} : {}),
    ...(native || look === 'web-google' ? {minHeight: PROVIDER_HEIGHT} : look === 'web-microsoft' ? {minHeight: control.field} : {}),
    ...(look === 'web-microsoft' ? {backdropFilter: `blur(${GLASS_BLUR}px)`, WebkitBackdropFilter: `blur(${GLASS_BLUR}px)`} : {}),
    ...(look === 'web-google' ? {animation: `wedgeStagger ${ENTRANCE_MS}ms ${B.ease} ${ENTRANCE_DELAY}ms backwards`} : {}),
  };
}

const nativeApple = '[data-rv-signin-look="native-apple"]';
const webGoogle = ':is([data-rv-signin-look="web-google"], [data-rv-signin-look="fallback-google"])';
const webSecondary = ':is([data-rv-signin-look="web-microsoft"], [data-rv-signin-look="web-apple"])';
export const WEDGE_SIGNIN_BUTTON_CSS = `
@keyframes wedgeStagger { from { opacity: 0; transform: translateY(${STAGGER_Y}px); } to { opacity: 1; transform: translateY(0); } }
${nativeApple}:hover:not(:disabled) { background: ${B.signinDarkHover} !important; transform: translateY(-1px); box-shadow: ${B.signinHoverShadow}; }
${webGoogle}:hover:not(:disabled) { background: ${B.signinLightHover} !important; transform: translateY(-1px); box-shadow: ${B.signinHoverShadow}; }
${webSecondary}:hover:not(:disabled) { background: ${B.glassHover} !important; border-color: ${B.glassHoverBorder} !important; transform: translateY(-1px); }
@media (prefers-reduced-motion: reduce) { [data-rv-appearance="wedge-signin"] { animation: none !important; } }
`;
